/**
 * useOrganizationConfig
 * Loads the current user's organization-level configuration (e.g. whether
 * form submission via the portal is enabled) once per session and caches it.
 *
 * Defaults to "enabled" while loading or on error / for users without an
 * organization (e.g. SuperAdmin) so portal UI is never hidden by accident.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import organizationService from '../api/services/organizationService';

// Module-level cache keyed by organizationId so the org is only fetched once
// across the pages that gate on it (submissions list, work order detail, etc.).
const cache = new Map();

/** Drop the cached config for an org so the next mount refetches it. */
export const invalidateOrganizationConfig = (orgId) => {
  if (orgId != null) cache.delete(orgId);
};

export const useOrganizationConfig = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId ?? null;

  const [config, setConfig] = useState(() =>
    orgId != null && cache.has(orgId) ? cache.get(orgId) : null
  );
  const [loading, setLoading] = useState(orgId != null && !cache.has(orgId));

  useEffect(() => {
    let active = true;

    if (orgId == null) {
      setConfig(null);
      setLoading(false);
      return undefined;
    }

    if (cache.has(orgId)) {
      setConfig(cache.get(orgId));
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    organizationService
      .getOrganizationById(orgId)
      .then((org) => {
        // Treat anything other than an explicit `false` as enabled so the
        // portal keeps working before the API change ships. The submission-metadata
        // edit flags default to `false` (restricted) when missing — admins opt in.
        const cfg = {
          enablePortalFormSubmission: org?.enablePortalFormSubmission !== false,
          allowEditSubmissionLocation: org?.allowEditSubmissionLocation === true,
          allowEditSubmissionDateTime: org?.allowEditSubmissionDateTime === true,
          allowEditSubmissionSubmitter: org?.allowEditSubmissionSubmitter === true,
        };
        cache.set(orgId, cfg);
        if (active) {
          setConfig(cfg);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setConfig(null);
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [orgId]);

  const enablePortalFormSubmission = config ? config.enablePortalFormSubmission : true;
  const allowEditSubmissionLocation = config ? config.allowEditSubmissionLocation : false;
  const allowEditSubmissionDateTime = config ? config.allowEditSubmissionDateTime : false;
  const allowEditSubmissionSubmitter = config ? config.allowEditSubmissionSubmitter : false;

  return {
    enablePortalFormSubmission,
    allowEditSubmissionLocation,
    allowEditSubmissionDateTime,
    allowEditSubmissionSubmitter,
    loading,
  };
};

export default useOrganizationConfig;
